import { ACCESSIBILITY_ARGS, Arg, Metadata, pick } from '../../../utils/spds/metadata';
import { LOADING_SPINNER_COLORS, NEUTRAL } from '../../../utils/spds/constants/loading-spinner';
import { LOADING_SPINNER_SIZES, MEDIUM } from '../../../utils/spds/constants/sizes';
import FlexRow from '../layout/flex/row.gjs';
import Super from '../core/base.gjs';
import styles from './loading-spinner.module.scss';

const METADATA = Metadata.define({
  description: `
    Animated loading spinner that indicates content is being loaded in the background.

    Use when the expected wait time is more than a second. Only one spinner should be visible
    on a page at a time.
  `,
  extends: [pick(ACCESSIBILITY_ARGS, [{ name: 'ariaLabel', default: 'Loading' }])],
  meta: {
    color: Arg.oneOf(LOADING_SPINNER_COLORS, {
      defaultValue: NEUTRAL,
      description: 'Spinner color variant',
    }),
    size: Arg.oneOf(LOADING_SPINNER_SIZES, {
      defaultValue: MEDIUM,
      description: 'Spinner size variant',
    }),
  },
  name: 'Loading Spinner',
  resources: {
    figma:
      'https://www.figma.com/design/g4zf0Vkj39VcCyleokv2rl/%E2%9D%96-Air-Web-Components?m=auto&node-id=16047-84922&t=ADL8oK1s8842xDHJ-1',
    zeroheight: 'https://www.simplepractice.design/1d0abbc0a/v/61618/p/59e0fa-spinner',
  },
});

export default class SpdsIndicatorLoadingSpinner extends Super {
  static metadata = this.setMetadata(METADATA, import.meta.url);
  baseClass = 'spds-indicator-loading-spinner';
  classesToMap = ['color', 'size'];
  styles = styles;

  get ariaLabel() {
    return this.argOrDefault('ariaLabel');
  }

  get color() {
    return this.argOrDefault('color');
  }

  get size() {
    return this.argOrDefault('size');
  }

  <template>
    <FlexRow
      @alignItems="center"
      @fitContent={{true}}
      @id={{this.id}}
      @inline={{true}}
      @justifyContent="center"
      @tag="span"
      aria-label={{this.ariaLabel}}
      class={{this.classes}}
      data-test-spds-indicator-loading-spinner={{this.id}}
      role="status"
      {{this.setupComponent}}
      {{this.validateArguments}}
      ...attributes
    >
      <svg
        aria-hidden="true"
        class="spds-indicator-loading-spinner-icon"
        data-test-spds-indicator-loading-spinner-icon
        viewBox="0 0 24 24"
      >
        <circle
          cx="12"
          cy="12"
          fill="none"
          r="10"
          stroke="currentColor"
          stroke-dasharray="47 16"
          stroke-linecap="round"
        />
      </svg>
    </FlexRow>
  </template>
}

export const LOADING_SPINNER_METADATA = SpdsIndicatorLoadingSpinner.metadata;
